import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  X,
  UtensilsCrossed,
  CalendarX,
  ChevronRight,
  Loader2,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Notification {
  Title: string;
  Message: string;
  Child_Name: string[];
  Screen_Name?: string;
}

export function getNotifNavPath(screenName: string | undefined, role: string): string | null {
  const s = screenName?.toLowerCase().trim();
  const r = role?.toLowerCase().replace(/[\s_]+/g, "");
  if (!s) return null;

  const map: Record<string, Record<string, string> & { default?: string }> = {
    child:             { parent: "/children", default: "/user-management" },
    subscription:      { parent: "/parent-plans", default: "/pricing-management" },
    "subscription data": { telecaller: "/subscription-data", default: "/subscription-data" },
    verification:      { parent: "/verification", default: "/verification" },
    lunch:             { parent: "/parent-lunch-updates", default: "/lunch-updates" },
    holiday:           { parent: "/parent-holidays", default: "/holidays" },
    holidays:          { parent: "/parent-holidays", default: "/holidays" },
    leaves:            { default: "/leaves" },
    pickups:           { default: "/pickups" },
    telerecords:       { default: "/tele-record" },
    leads:             { default: "/leads" },
    "lunch tracking":  { parent: "/parent-lunch-updates", default: "/track-delivery" }
  };

  const entry = map[s];
  if (!entry) return null;
  return entry[r] ?? entry.default ?? null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function notifMeta(title: string): {
  Icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  dot: string;
  accentBar: string;
  tagBg: string;
  tagText: string;
} {
  const t = title.toLowerCase();
  if (t.includes("lunch") || t.includes("food")) {
    return {
      Icon: UtensilsCrossed,
      iconBg: "bg-amber-50",
      iconColor: "text-amber-500",
      dot: "bg-amber-400",
      accentBar: "bg-amber-400",
      tagBg: "bg-amber-50 border-amber-200",
      tagText: "text-amber-700",
    };
  }
  if (t.includes("subscription") || t.includes("expir")) {
    return {
      Icon: CalendarX,
      iconBg: "bg-rose-50",
      iconColor: "text-rose-500",
      dot: "bg-rose-400",
      accentBar: "bg-rose-400",
      tagBg: "bg-rose-50 border-rose-200",
      tagText: "text-rose-700",
    };
  }
  return {
    Icon: Bell,
    iconBg: "bg-brand-50",
    iconColor: "text-brand-500",
    dot: "bg-brand-400",
    accentBar: "bg-brand-400",
    tagBg: "bg-brand-50 border-brand-200",
    tagText: "text-brand-700",
  };
}

// ─── Notification Item ────────────────────────────────────────────────────────

export function NotificationItem({
  notif,
  compact = false,
}: {
  notif: Notification;
  compact?: boolean;
}) {
  const { Icon, iconBg, iconColor, dot } = notifMeta(notif.Title);

  return (
    <div className="flex gap-3 px-4 py-3.5 hover:bg-gray-50/80 transition-colors">
      <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
        <Icon className={`w-4 h-4 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[13px] font-semibold text-gray-900 leading-snug">
            {notif.Title}
          </p>
          <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${dot}`} />
        </div>
        <p className={`text-[12px] text-gray-500 mt-0.5 leading-relaxed ${compact ? "line-clamp-2" : ""}`}>
          {notif.Message}
        </p>
        {notif.Child_Name?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {notif.Child_Name.map((name) => (
              <span
                key={name}
                className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-gray-100 text-[10px] font-semibold text-gray-600 capitalize"
              >
                {name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Dropdown ─────────────────────────────────────────────────────────────────

interface NotificationsDropdownProps {
  fetchFn: () => Promise<any>;
  viewAllPath?: string;
}

export default function NotificationsDropdown({
  fetchFn,
  viewAllPath = "/notifications",
}: NotificationsDropdownProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
  function handleClick(e: MouseEvent) {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      setOpen(false);
    }
  }

  if (open) document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}, [open]);

  // Fetch on first open
  useEffect(() => {
    if (!open || fetched) return;
    setLoading(true);
    fetchFn()
      .then((res: any) => {
        if (res?.status === 200 && Array.isArray(res.data)) {
          setNotifications(res.data as Notification[]);
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
        setFetched(true);
      });
  }, [open, fetched]);

  const preview = notifications.slice(0, 3);
  const hasMore = notifications.length > 3;

  return (
    <div ref={ref} className="relative">
      {/* Bell button */}
      <button
        onClick={() => navigate('/notifications')}
        className="relative p-2 text-gray-500 hover:bg-gray-100 rounded-xl transition-colors"
      >
        <Bell className="w-5 h-5" />
        {(notifications.length > 0 || !fetched) && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-bold text-gray-900">Notifications</span>
              {notifications.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[10px] font-bold">
                  {notifications.length}
                </span>
              )}
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="divide-y divide-gray-50">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <Loader2 className="w-6 h-6 text-gray-300 animate-spin" />
                <p className="text-xs text-gray-400">Loading…</p>
              </div>
            ) : preview.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-gray-300" />
                </div>
                <p className="text-sm font-medium text-gray-500">All caught up!</p>
                <p className="text-xs text-gray-400">No new notifications</p>
              </div>
            ) : (
              preview.map((n, i) => (
                <NotificationItem key={i} notif={n} compact />
              ))
            )}
          </div>

          {/* Footer */}
          {!loading && notifications.length > 0 && (
  <div
    onClick={(e) => { e.stopPropagation(); setOpen(false); navigate(viewAllPath); }}
    className="border-t border-gray-100 bg-gray-50/60 w-full flex items-center justify-center gap-1.5 py-3 text-[13px] font-semibold text-brand-500 hover:text-brand-700 hover:bg-brand-50/60 transition-colors cursor-pointer select-none"
  >
    <span className="pointer-events-none flex items-center gap-1.5">
      View all notifications
      {hasMore && (
        <span className="text-brand-400 text-[11px]">
          (+{notifications.length - 3} more)
        </span>
      )}
      <ChevronRight className="w-3.5 h-3.5" />
    </span>
  </div>
)}
        </div>
      )}
    </div>
  );
}
